import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from "../../ui/use-toast"
import { getCsrfToken } from '../../utils/csrf'
import { CryptoInvestment, Investment, TesouroDiretoInvestment } from '../models/ModelInvestments'

export const useEditInvestments = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [investments, setInvestments] = useState<Investment[]>([])
  const [cryptoInvestments, setCryptoInvestments] = useState<CryptoInvestment[]>([])
  const [tesouroDiretoInvestments, setTesouroDiretoInvestments] = useState<TesouroDiretoInvestment[]>([])
  const [countries, setCountries] = useState<string[]>([])
  const [negotiationMarkets, setNegotiationMarkets] = useState<Record<string, string[]>>({})
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<number | null>(null)
  const [availableTypes, setAvailableTypes] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const fetchAvailableTypes = async () => {
      try {
        const csrfToken = await getCsrfToken()
        const response = await fetch('/api/investments/edit/', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            'X-Investment-Type': 'none'
          },
        })

        const data = await response.json()
        if (data.success) {
          setAvailableTypes(data.data.available_types)
        }
      } catch (error) {
        console.error('Error fetching investment types:', error)
      }
    }

    fetchAvailableTypes()
  }, [])

  const handleAssetTypeSelection = async (type: string) => {
    try {
      const csrfToken = await getCsrfToken()
      const response = await fetch('/api/investments/edit/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Investment-Type': type || 'none'
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
      }

      let data
      try {
        data = await response.json()
      } catch (e) {
        throw new Error(`Invalid JSON response: ${await response.text()}`)
      }

      if (data.success) {
        setSelectedType(type)
        if (type === 'stock') {
          setInvestments(data.data.investments || [])
          setCountries(data.data.countries || [])
          setNegotiationMarkets(data.data.negotiation_markets || {})
        } else if (type === 'crypto') {
          setCryptoInvestments(data.data.crypto || [])
        } else if (type === 'tesouro-direto') {
          setTesouroDiretoInvestments(data.data.tesouro_direto || [])
        }
      } else {
        throw new Error(data.error || 'Unknown error occurred')
      }
    } catch (error) {
      console.error('Error fetching investments:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to fetch investments. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      const csrfToken = await getCsrfToken()
      const type = selectedType === 'digital-coin' ? 'crypto' : selectedType || ''
      
      const response = await fetch('/api/investments/edit/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Investment-Type': type
        },
        body: JSON.stringify({
          data: type === 'stock' ? { investments } :
                type === 'crypto' ? { crypto: cryptoInvestments } :
                { tesouro_direto: tesouroDiretoInvestments }
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
      }

      let data
      try {
        data = await response.json()
      } catch (e) {
        throw new Error(`Invalid JSON response: ${await response.text()}`)
      }

      if (data.success) {
        toast({
          title: "Success",
          description: "Investments updated successfully.",
        })
        navigate('/investments')
      } else {
        throw new Error(data.error || 'Unknown error occurred')
      }
    } catch (error) {
      console.error('Error saving changes:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save changes. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const csrfToken = await getCsrfToken()
      const type = selectedType === 'digital-coin' ? 'crypto' : selectedType || ''
      
      const response = await fetch('/api/investments/edit/', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Investment-Type': type
        },
        body: JSON.stringify({ id })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
      }

      let data
      try {
        data = await response.json()
      } catch (e) {
        throw new Error(`Invalid JSON response: ${await response.text()}`)
      }

      if (data.success) {
        toast({
          title: "Success",
          description: "Investment deleted successfully.",
        })
        setDeleteConfirmation(null)
        
        // Refresh the investments list after deletion
        await handleAssetTypeSelection(selectedType || '')
      } else {
        throw new Error(data.error || 'Unknown error occurred')
      }
    } catch (error) {
      console.error('Error deleting investment:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete investment. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleInputChange = (index: number, field: keyof Investment, value: string | number) => {
    setInvestments(prev => prev.map((inv, i) => i === index ? { ...inv, [field]: value } : inv))
  }

  const handleCryptoInputChange = (index: number, field: keyof CryptoInvestment, value: string | number) => {
    setCryptoInvestments(prev => prev.map((inv, i) => i === index ? { ...inv, [field]: value } : inv))
  }

  const handleTesouroDiretoInputChange = (index: number, field: keyof TesouroDiretoInvestment, value: string | number | boolean) => {
    setTesouroDiretoInvestments(prev => prev.map((inv, i) => i === index ? { ...inv, [field]: value } : inv))
  }

  return {
    investments,
    cryptoInvestments,
    tesouroDiretoInvestments,
    countries,
    negotiationMarkets,
    selectedType,
    deleteConfirmation,
    setDeleteConfirmation,
    handleInputChange,
    handleCryptoInputChange,
    handleTesouroDiretoInputChange,
    handleSubmit,
    handleDelete,
    handleAssetTypeSelection,
    availableTypes,
  }
}